import type { GuideDetectParams } from "./guideDetection";

// ── Detection params ─────────────────────────────────────────────────────────
// Tune these via the in-app debug panel (press [D] on the camera widget in dev).
// Copy the resulting values back here once they feel right.

/** Defaults for the guide-box seed-and-expand detector (guideDetection.ts).
 *  Guide is paper-shaped on a 4:3 preview: portrait aspect ≈ 0.75. */
export const PAPER_GUIDE_PARAMS: GuideDetectParams = {
  colorTolerance: 20,
  gradientThreshold: 60,
  stripAcceptRatio: 0.5,
  guideW: 0.42,
  guideH: 0.75,
  minFillRatio: 0.25,
  minExpansion: 0,
  guideCoverMargin: 0.15,
};

// ── Capture ──────────────────────────────────────────────────────────────────
/**
 * JPEG quality used when the cropper writes the final image. The capture
 * itself is encoded as PNG (lossless) so the crop step is the only lossy
 * pass — otherwise we'd be re-encoding a JPEG into another JPEG.
 */
export const CROP_JPEG_QUALITY = 0.85;

/**
 * Default extra margin around the detected bbox when pre-cropping in
 * capture(). Live value is tunable via the debug panel.
 */
export const CAPTURE_BBOX_PADDING = 0.03;

/** How long (ms) a detection must persist before auto-capture fires. */
export const DETECTION_HOLD_MS = 1000;

/**
 * Safety timer: if detection never settles within this window after the
 * camera opens, capture whatever's on screen anyway. Gives the user a
 * bounded wait rather than leaving them stuck if detection never fires.
 */
export const AUTO_CAPTURE_TIMEOUT_MS = 5000;

/**
 * How long (ms) the score must stay above threshold before the countdown
 * animation starts. Prevents the animation restarting on brief score flickers.
 */
export const DETECTION_CONFIDENCE_MS = 400;

// ── Camera ───────────────────────────────────────────────────────────────────
/** Preferred camera constraints — rear cam on mobile, fallback to default. */
export const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: "environment" },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

/** Skip N-1 animation frames between detection runs (3 → ~20fps on 60fps display). */
export const EDGE_DETECTION_FRAME_SKIP = 3;
